import { useCallback } from 'react';
import Router, { useRouter } from 'next/router';

import moment from 'moment';
import 'moment/locale/el';
import 'moment/locale/de';
import 'moment/locale/fr';
import 'moment/locale/ru';
import 'moment/locale/it';
import 'moment/locale/cs';

import en from '../locales/en.json';
import el from '../locales/el.json';
import de from '../locales/de.json';
import fr from '../locales/fr.json';
import ru from '../locales/ru.json';
import it from '../locales/it.json';
import cs from '../locales/cs.json';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, normalizeLocale } from './class/RequestLocale';

// UI texts that the site itself owns (buttons, labels, error pages). Texts that come from the
// backend (titles, descriptions, menus, builder settings) are already translated by the API.
// One json file per locale in /locales; add the new file here and in config/locales.js.
// Run `npm run check:locales` to make sure every language has the same keys.
const dictionaries: Record<string, Record<string, string>> = { en, el, de, fr, ru, it, cs };

export type TranslationParams = Record<string, string | number>;

// Missing in the requested language -> default language -> the key itself, so a gap never blanks the UI.
export function translate ( locale: string | null | undefined, key: string, params?: TranslationParams ): string {
  const text = dictionaries[normalizeLocale( locale )]?.[key] ?? dictionaries[DEFAULT_LOCALE]?.[key] ?? key;
  if ( params == null ) {
    return text;
  }
  return text.replace( /\{(\w+)\}/g, ( match, name ) => ( params[name] != null ? String( params[name] ) : match ) );
}

// Plural forms live in `<key>.<category>` keys, with the categories of Intl.PluralRules for the language:
// every language has `.one` and `.other` ("{count} night" / "{count} nights"); languages with more forms add
// them, e.g. Russian `.few` / `.many` (1 ночь, 2 ночи, 5 ночей). A category the dictionary does not have falls
// back to `.other`.
export function translatePlural ( locale: string | null | undefined, key: string, count: number, params?: TranslationParams ): string {
  const normalized = normalizeLocale( locale );
  let category = count === 1 ? 'one' : 'other';
  try {
    category = new Intl.PluralRules( intlLocale( normalized ) ).select( count );
  } catch ( e ) {
    // no Intl.PluralRules: keep the simple one/other rule
  }
  const pluralKey = key + '.' + category;
  const hasForm = dictionaries[normalized]?.[pluralKey] != null || dictionaries[DEFAULT_LOCALE]?.[pluralKey] != null;
  return translate( locale, hasForm ? pluralKey : key + '.other', { ...params, count } );
}

// In components: const t = useT(); ... {t( 'common.showPhotos' )} ... {t.plural( 'search.nights', 3 )}
export function useT () {
  const { locale } = useRouter();
  const t = useCallback(
    ( key: string, params?: TranslationParams ) => translate( locale, key, params ),
    [ locale ]
  );
  return Object.assign( t, {
    plural: ( key: string, count: number, params?: TranslationParams ) => translatePlural( locale, key, count, params )
  } );
}

// URL prefix of a locale: '' for the default language, '/el' otherwise. Internal links that are built as
// plain strings (origin + slug) need it, because only next/link and router.push add it by themselves.
export function localePrefix ( locale: string | null | undefined ): string {
  const normalized = normalizeLocale( locale );
  return normalized === DEFAULT_LOCALE ? '' : '/' + normalized;
}

export function useLocalePrefix (): string {
  const { locale } = useRouter();
  return localePrefix( locale );
}

// Internal link built as a plain string (e.g. a property's sourcePath "/Villa-Rogdia") with the current
// language prefix ("/el/Villa-Rogdia"). Plain <a href> and window.open do not add it; next/link does.
// External urls, anchors and paths that already carry the prefix are returned unchanged.
export function localizePath ( path: string, locale: string | null | undefined ): string {
  if ( typeof path !== 'string' || !path.startsWith( '/' ) || path.startsWith( '//' ) ) {
    return path;
  }
  const prefix = localePrefix( locale );
  if ( prefix === '' || path === prefix || path.startsWith( prefix + '/' ) || path.startsWith( prefix + '?' ) ) {
    return path;
  }
  return prefix + path;
}

export function useLocalizePath (): ( path: string ) => string {
  const { locale } = useRouter();
  return useCallback( ( path: string ) => localizePath( path, locale ), [ locale ] );
}

// The guests summary ("2 adults, 1 infant") is kept in component state, but never as finished text:
// state holds a language-neutral token such as 'adults:2;infants:1' ('' = nothing chosen yet) and the
// text is built at render time, so it follows the page language.
export function guestsToken ( ...pairs: [ string, number ][] ): string {
  return pairs.filter( ( [ , count ] ) => count > 0 ).map( ( [ kind, count ] ) => kind + ':' + count ).join( ';' );
}

export function guestsLabel ( t: ( key: string, params?: TranslationParams ) => string, token: string | null | undefined ): string {
  const plural = ( t as any ).plural as ( key: string, count: number ) => string;
  if ( !token || token === 'Who' || token === 'Who will stay' ) {
    return t( 'search.whoWillStay' );
  }
  return token.split( ';' ).map( ( part ) => {
    const [ kind, count ] = part.split( ':' );
    return plural( 'guests.' + kind, Number( count ) );
  } ).join( ', ' );
}

// Locale of the current page (Next i18n), normalised to one we serve.
export function useLocale (): string {
  const { locale } = useRouter();
  return normalizeLocale( locale );
}

// Formats a date in the page language. The locale is set on the moment instance, never globally,
// because the server renders requests of different languages at the same time.
export function formatMomentDate ( locale: string | null | undefined, date: any, format: string ): string {
  return moment( date ).locale( normalizeLocale( locale ) ).format( format );
}

// Locale tag for Intl / toLocale*String
const INTL_LOCALES: Record<string, string> = { en: 'en-US', el: 'el-GR', de: 'de-DE', fr: 'fr-FR', ru: 'ru-RU', it: 'it-IT', cs: 'cs-CZ' };

export function intlLocale ( locale: string | null | undefined ): string {
  return INTL_LOCALES[normalizeLocale( locale )] ?? INTL_LOCALES[DEFAULT_LOCALE];
}

// Browser calls to the /apiloggia proxy carry the page language as ?locale=xx (nothing for the default
// language). Read it from the router, which follows client-side navigation; __NEXT_DATA__ only holds the
// locale of the first page load. Returns the url unchanged on the server, for the default language, or when
// the url already has a locale.
export function withProxyLocale ( url: string ): string {
  if ( typeof window === 'undefined' || /[?&]locale=/.test( url ) ) {
    return url;
  }
  let locale = ( window as any ).__NEXT_DATA__?.locale;
  try {
    locale = Router.locale ?? locale;
  } catch ( e ) {
    // router not initialised yet: keep the first page load's locale
  }
  if ( !locale || locale === DEFAULT_LOCALE || !SUPPORTED_LOCALES.includes( locale ) ) {
    return url;
  }
  return url + ( url.includes( '?' ) ? '&' : '?' ) + 'locale=' + locale;
}
